import React, { useState, useEffect, useRef } from 'react';
import { Chart, registerables } from 'chart.js';
import { api } from '../api/gridApi';

Chart.register(...registerables);

const QUICK = [
  'When should I run my AC tonight?',
  'Is my solar panel output normal today?',
  'How can I cut my electricity bill this month?',
  'What is the current electricity tariff?',
];

const RECS = [
  { text: 'Shift washing machine to 2 AM', sub: 'Save ₹18 · Off-peak tariff available', dot: 'fd-green' },
  { text: 'Charge EV between 1–4 AM', sub: 'Save ₹42 · Lowest tariff window', dot: 'fd-green' },
  { text: 'AC temperature +2°C saves ₹60/mo', sub: 'Set to 26°C instead of 24°C', dot: 'fd-amber' },
];

const STATES = ['UP', 'Maharashtra', 'Karnataka', 'Delhi', 'Tamil_Nadu', 'Gujarat'];

export default function Advisor() {
  const [messages, setMessages] = useState([{
    role: 'ai',
    text: "Hello! I'm your HomeGrid AI advisor powered by real weather data and live grid intelligence. I can help you reduce your electricity bill, optimize solar usage, find the best tariff windows, and monitor your energy. What would you like to know?",
    time: new Date().toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })
  }]);
  const [input, setInput] = useState('');
  const [typing, setTyping] = useState(false);
  const [state, setState] = useState('UP');
  const [gridLive, setGridLive] = useState(null);
  const chatEndRef = useRef(null);
  const homeChartRef = useRef(null);
  const homeChartInst = useRef(null);

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, typing]);

  useEffect(() => {
    api.getLiveGrid().then(r => setGridLive(r.data));
  }, []);

  useEffect(() => {
    if (!homeChartRef.current) return;
    if (homeChartInst.current) { homeChartInst.current.destroy(); homeChartInst.current = null; }
    const data = [0.2,0.1,0.1,0.1,0.15,0.3,0.8,1.2,0.9,0.6,0.5,0.4,0.7,0.6,0.5,0.6,0.8,1.4,1.6,1.3,1.1,0.9,0.6,0.3];
    homeChartInst.current = new Chart(homeChartRef.current, {
      type: 'bar',
      data: {
        labels: Array.from({ length: 24 }, (_, i) => `${i}:00`),
        datasets: [{ label: 'Usage (kWh)', data, backgroundColor: data.map(v => v > 1 ? 'rgba(245,166,35,1)' : 'rgba(0,220,100,0.15)'), borderColor: data.map(v => v > 1 ? 'rgba(245,166,35,1)' : 'rgba(0,220,100,1)'), borderWidth: 1, borderRadius: 2 }]
      },
      options: { responsive: true, maintainAspectRatio: false, plugins: { legend: { display: false }, tooltip: { backgroundColor: '#111f14', borderColor: 'rgba(0,220,100,0.25)', borderWidth: 1, titleColor: '#00dc64', bodyColor: 'rgba(238,244,240,0.7)' } }, scales: { x: { grid: { color: 'rgba(0,220,100,0.08)' }, ticks: { color: 'rgba(238,244,240,0.35)', font: { size: 9 }, maxTicksLimit: 8 } }, y: { grid: { color: 'rgba(0,220,100,0.08)' }, ticks: { color: 'rgba(238,244,240,0.35)', font: { size: 9 } } } } }
    });
  }, []);

  async function send(q) {
    const question = q || input.trim();
    if (!question) return;
    setInput('');
    const t = new Date().toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' });
    const newMessages = [...messages, { role: 'user', text: question, time: t }];
    setMessages(newMessages);
    setTyping(true);

    // Build history for conversation memory
    const history = newMessages.slice(-8).map(m => ({
      role: m.role === 'ai' ? 'assistant' : 'user',
      content: m.text
    }));

    const answer = await api.askAdvisor(question, history, state);
    setTyping(false);
    setMessages(prev => [...prev, {
      role: 'ai',
      text: answer,
      time: new Date().toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })
    }]);
  }

  const isLive = gridLive?.source === 'openmeteo_live';

  return (
    <div className="page">
      <div className="page-header">
        <div>
          <div className="page-title">HomeGrid <span>AI</span></div>
          <div className="page-sub">
            {isLive ? `Live · ${gridLive?.temperature}°C · ${gridLive?.radiation} W/m² radiation · ${state} DISCOM rates` : 'Personal energy intelligence advisor'}
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <select
            value={state}
            onChange={e => setState(e.target.value)}
            style={{ background: 'var(--card)', border: '1px solid var(--border2)', color: 'var(--green)', fontFamily: 'var(--mono)', fontSize: 11, padding: '6px 12px', cursor: 'pointer' }}
          >
            {STATES.map(s => <option key={s} value={s}>{s.replace('_', ' ')}</option>)}
          </select>
        </div>
      </div>

      <div className="stat-row">
        <div className="stat-card info">
          <div className="sc-label">Solar Radiation Now</div>
          <div className="sc-val">{gridLive?.radiation || '—'}</div>
          <div className="sc-change up">{isLive ? '▲ Real weather data' : 'Simulated'}</div>
          <div className="sc-sub">W/m² · {gridLive?.location || 'India'}</div>
        </div>
        <div className="stat-card">
          <div className="sc-label">Outside Temperature</div>
          <div className="sc-val">{gridLive?.temperature || '—'}</div>
          <div className="sc-change flat">{isLive ? 'Live sensor' : 'Simulated'}</div>
          <div className="sc-sub">°C · Affects grid demand</div>
        </div>
        <div className="stat-card info">
          <div className="sc-label">Best Tariff Tonight</div>
          <div className="sc-val">2 AM</div>
          <div className="sc-change flat">{state} DISCOM rates loaded</div>
          <div className="sc-sub">₹2.10/kWh lowest window</div>
        </div>
      </div>

      <div className="grid3">
        <div className="panel">
          <div className="panel-header">
            <span className="panel-title">HomeGrid AI Advisor</span>
            <span className="panel-badge pb-green">Online · Memory Active</span>
          </div>
          <div className="quick-prompts">
            {QUICK.map(q => <button key={q} className="qp-btn" onClick={() => send(q)}>{q}</button>)}
          </div>
          <div className="chat-messages">
            {messages.map((m, i) => (
              <div key={i} className={`chat-msg${m.role === 'user' ? ' user' : ''}`}>
                <div className={`msg-avatar ${m.role === 'ai' ? 'av-ai' : 'av-user'}`}>{m.role === 'ai' ? 'AI' : 'You'}</div>
                <div>
                  <div className={`msg-bubble ${m.role === 'ai' ? 'msg-ai' : 'msg-user-b'}`}>{m.text}</div>
                  <div className="msg-time">{m.time}</div>
                </div>
              </div>
            ))}
            {typing && (
              <div className="chat-msg">
                <div className="msg-avatar av-ai">AI</div>
                <div className="msg-bubble msg-ai"><div className="typing"><span /><span /><span /></div></div>
              </div>
            )}
            <div ref={chatEndRef} />
          </div>
          <div style={{ display: 'flex', gap: 8, marginTop: 4 }}>
            <input className="advisor-input" value={input} onChange={e => setInput(e.target.value)} onKeyDown={e => e.key === 'Enter' && send()} placeholder="Ask in Hindi or English..." />
            <button className="btn btn-solid" onClick={() => send()}>Ask AI</button>
          </div>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div className="panel">
            <div className="panel-header"><span className="panel-title">Today's Usage</span></div>
            <div style={{ position: 'relative', height: 160 }}><canvas ref={homeChartRef} /></div>
          </div>
          <div className="panel">
            <div className="panel-header"><span className="panel-title">Smart Recommendations</span></div>
            <div className="fault-list">
              {RECS.map((r, i) => (
                <div className="fault-item" key={i}>
                  <div className={`fault-dot ${r.dot}`} />
                  <div>
                    <div className="fault-info-name">{r.text}</div>
                    <div className="fault-info-loc">{r.sub}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
